/*
 * From a window of messages to the rows the timeline draws. Pure and without a DOM, because every
 * rule worth arguing about lives here: where the day dividers fall, where the unread divider stays,
 * which pending sends are still pending, and whether the top of the window is the top of the
 * member's history.
 *
 * Four rules are load-bearing (spec 12 §4.3, UX §5.3):
 *   - which messages a *timeline* draws at all is decided first, in `timelineMessages`, and every
 *     other rule is then asked of those rows only — so a day that holds nothing a reader can see
 *     gets no day pill (D-10 D5). The thread pane builds its rows from the same function and leaves
 *     that option out: there, the replies are the content;
 *   - the unread divider is keyed on the seq it was created with, never on a count, so it stays
 *     where the reader entered while messages keep arriving below it;
 *   - a pending row disappears the moment its own message is in the window — the echo is collapsed
 *     by matching `clientMsgId`, never by a timer;
 *   - a message continues the block above it only when the row directly above is a message row of
 *     the same person: whatever comes between two messages — a divider, an attachment card, a row
 *     still on its way out — is a break, and the next message starts with its header again.
 */

import type { OutboxItem } from "../../../core/drafts/types.ts"
import type { MessageView } from "../wire/views.ts"
import type { Row } from "./types.ts"

/** Two messages by the same author closer together than this share one header. */
const GROUPING_WINDOW_MS = 5 * 60 * 1000
/** Inside one block, a pause at least this long earns the next row its own visible time. */
const TIME_GAP_MS = 2 * 60 * 1000

export interface BuildOpts {
	/**
	 * The seq the unread divider was pinned at: the reader's last read seq when the conversation was
	 * opened. 0 means no divider.
	 */
	unreadAnchorSeq: number
	/** The first seq this member may see; a late joiner's history starts above nothing. */
	visibleFromSeq: number
	/** False when the loaded window reaches the beginning of the visible history. */
	hasMoreBefore: boolean
	/** This conversation's unacknowledged sends, in send order. */
	pending?: OutboxItem[]
	/**
	 * Which day a timestamp belongs to, in the reader's time zone. Injected so the rule is testable
	 * without an Intl round trip; the interface passes core's zone-aware formatter.
	 */
	dayKey: (ms: number) => string
	/**
	 * The seq a permalink named that the loaded window does not hold, because the message is gone:
	 * a divider says so at the place it had. 0 means there is none.
	 */
	goneSeq?: number
	/**
	 * True when these messages are a conversation's *timeline*, which is the one surface that leaves
	 * rows out: a thread reply belongs to its thread and the creation row under the beginning of the
	 * conversation says what the divider above it says (`timelineMessages`). A thread pane draws its
	 * replies and passes nothing, because there the replies are the whole content.
	 */
	timeline?: boolean
}

/**
 * The row that says where the visible history begins — and, where both boundaries fall together, the
 * day it begins on as well.
 *
 * One boundary is one rule (VISUAL.md §1.3): "Beginning of the conversation" and a "Today" pill 40 px
 * under it were two full-width rules for one fact (X-B4 B4-16). The day travels on the start row
 * rather than in `Row`'s own `historyStart` variant because `list/types.ts` is the list engine's file;
 * `boundaryDay` below is the one reader of it, so nothing outside these two files has to know.
 */
export interface HistoryStartRow {
	kind: "historyStart"
	visibleFromSeq: number
	/** The day the row also says; absent when a day divider of its own follows it. */
	dayStartMs?: number
}

/** The day a row says besides where the history begins, or 0 — `ConversationRow`'s one question. */
export function boundaryDay(row: Row): number {
	if (row.kind !== "historyStart" || !("dayStartMs" in row)) return 0
	return typeof row.dayStartMs === "number" ? row.dayStartMs : 0
}

/**
 * The day the start row carries, or 0 when a day divider of its own has to follow it: the visible
 * history begins at the oldest message of the window, which is not always the first message the
 * timeline *draws* — a thread reply or the creation row above it means there is history between the
 * two boundaries, and then they are two rows again.
 *
 * It is the *timeline's* rule, like `timelineMessages` above it: a thread pane asks for no start row
 * at all, and the one row it builds for itself says how many replies there are rather than a day.
 */
function startRowDay(
	all: readonly MessageView[],
	messages: readonly MessageView[],
	opts: BuildOpts,
): number {
	const first = messages[0]
	const oldest = all[0]
	if (opts.timeline !== true || opts.hasMoreBefore) return 0
	if (first === undefined || oldest === undefined) return 0
	return opts.dayKey(first.createdAt) === opts.dayKey(oldest.createdAt) ? first.createdAt : 0
}

export function buildRows(all: readonly MessageView[], opts: BuildOpts): Row[] {
	const rows: Row[] = []
	const messages = opts.timeline === true ? timelineMessages(all, !opts.hasMoreBefore) : all
	const startDay = startRowDay(all, messages, opts)
	if (!opts.hasMoreBefore) {
		const start: HistoryStartRow = { kind: "historyStart", visibleFromSeq: opts.visibleFromSeq }
		if (startDay !== 0) start.dayStartMs = startDay
		rows.push(start)
	}

	const divider = unreadDividerSeq(messages, opts.unreadAnchorSeq)
	const gone = opts.goneSeq ?? 0
	let goneDrawn = gone === 0
	// The day the start row has already said is not said again a row below it.
	let previousDay = startDay === 0 ? "" : opts.dayKey(startDay)
	for (const msg of messages) {
		if (!goneDrawn && msg.seq > gone) {
			rows.push({ kind: "gone", seq: gone })
			goneDrawn = true
		}
		const day = opts.dayKey(msg.createdAt)
		if (day !== previousDay) {
			rows.push({ kind: "day", dayStartMs: msg.createdAt })
			previousDay = day
		}
		if (divider !== 0 && msg.seq === divider) rows.push({ kind: "unread", seq: divider })
		const previous = rows.at(-1)
		const continues = continuesAfter(previous, msg)
		rows.push({
			kind: "message",
			seq: msg.seq,
			msg,
			continues,
			timeGap: continues && previous?.kind === "message" && isTimeGap(previous.msg, msg),
		})
	}
	if (!goneDrawn) rows.push({ kind: "gone", seq: gone })

	// Collapsing the echo costs a walk of the whole window, and it answers a question only a send
	// still in flight asks. Without one — the ordinary case, and the case on every arriving message —
	// there is nothing to collapse, so the walk is not made at all.
	const pending = opts.pending ?? []
	if (pending.length > 0) {
		const echoed = new Set(messages.map((msg) => msg.clientMsgId ?? "").filter((id) => id !== ""))
		for (const entry of pending) {
			if (echoed.has(entry.clientMsgId)) continue
			rows.push({ kind: "pending", clientMsgId: entry.clientMsgId })
		}
	}
	return rows
}

/**
 * The messages the timeline draws a row for, which is not every message of the window. Two never
 * appear, and both have to be taken out *here* rather than at the row that would draw them, because
 * a day pill is pushed from the rows that remain: a day holding nothing else would otherwise stand
 * over an empty stretch of list (D-10 D5).
 *
 *   - a **thread reply** belongs to its thread; only one its sender also sent to the conversation is
 *     a row here as well (the reducer counts the two the same way — `applyNewMessage`);
 *   - the **`conversation_created` row at the very top**, which says what "Beginning of the
 *     conversation" says one row above it (D-05 D11). Only there: a conversation whose creation row
 *     is further down the loaded window is one whose beginning is not on screen.
 */
function timelineMessages(
	all: readonly MessageView[],
	/** Whether the rows open with the history-start divider, i.e. whether the beginning is on screen. */
	atBeginning: boolean,
): readonly MessageView[] {
	// The wire leaves a root out or sends 0; both mean "not a reply".
	const shown = all.filter((msg) => (msg.threadRootId ?? 0) === 0 || msg.alsoToChannel === true)
	const first = shown[0]
	if (!atBeginning || first === undefined) return shown
	if (first.authorKind !== "system" || first.kind !== "conversation_created") return shown
	return shown.slice(1)
}

/**
 * Whether a message joins the block above it rather than opening one of its own: the row directly
 * above is a message row, by the same person, neither of the two deleted, and less than five
 * minutes older. Asking it of the previous *row* is what makes every divider a break — a rule about
 * the previous *message* would group across the day pill and across the unread line.
 *
 * A system message never groups: it is a sentence about the conversation, not an utterance in it.
 */
export function continuesAfter(previous: Row | undefined, msg: MessageView): boolean {
	if (previous?.kind !== "message") return false
	const before = previous.msg
	if (before.authorKind !== "user" || msg.authorKind !== "user") return false
	if (before.authorId !== msg.authorId) return false
	if (before.deletedAt !== undefined || msg.deletedAt !== undefined) return false
	return msg.createdAt - before.createdAt < GROUPING_WINDOW_MS
}

/** Whether a continuation row follows enough silence to deserve its own visible time. */
function isTimeGap(before: MessageView, msg: MessageView): boolean {
	return msg.createdAt - before.createdAt >= TIME_GAP_MS
}

/**
 * The seq the divider sits above: the first message the reader has not read. It is 0 — no divider —
 * when everything in the window was read, or when the anchor is below the loaded window and the
 * divider would be drawn at the top for no reason.
 */
function unreadDividerSeq(messages: readonly MessageView[], anchorSeq: number): number {
	if (anchorSeq <= 0) return 0
	const first = messages.find((msg) => msg.seq > anchorSeq)
	if (!first) return 0
	// Everything in the window is unread and older history exists above it: the divider would say
	// nothing the top of the window does not already say.
	const oldest = messages[0]
	if (oldest && oldest.seq === first.seq && anchorSeq + 1 < oldest.seq) return 0
	return first.seq
}
